<script>
import axios from '@/utils/axios';

const SESSION_LABELS = {
  full_day: 'Full Day',
  half_day_morning: 'Half Day (AM)',
  half_day_afternoon: 'Half Day (PM)',
  short_day_morning: 'Short Day (AM)',
  short_day_afternoon: 'Short Day (PM)',
};

export default {
  inject: ['setLoading'],
  data() {
    return {
      loading: true,
      requests: [],
      statusFilter: 'pending',
      statusOptions: ['pending', 'approved', 'rejected', 'cancelled'],
      tableColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'leave_type', label: 'Leave Type' },
        { key: 'dates', label: 'Dates' },
        { key: 'total_days', label: 'Days' },
        { key: 'session', label: 'Session' },
        { key: 'status', label: 'Status' },
        { key: 'reason', label: 'Reason' },
        { key: 'actions', label: '', width: '110px' },
      ],
      pagination: { current_page: 1, per_page: 20, last_page: 1, total: 0, links: [] },
      approveItem: null,
      rejectItem: null,
      rejectReason: '',
      rejectError: '',
    };
  },
  mounted() { this.fetchRequests(); },
  methods: {
    async fetchRequests(page = 1) {
      this.loading = true;
      try {
        const res = await axios.get('/hrms/leaves/team-requests', {
          params: { page, status: this.statusFilter || undefined },
        });
        const d = res.data.data;
        this.requests = d.data;
        this.pagination = { current_page: d.current_page, per_page: d.per_page, last_page: d.last_page, total: d.total, links: d.links };
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.loading = false; }
    },
    empName(row) {
      const e = row.employee || {};
      return e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ') || e.name || '-';
    },
    sessionLabel(s) { return SESSION_LABELS[s] || s; },
    cardColor(c) { return c || '#6366f1'; },

    confirmApprove(row) { this.approveItem = row; this.$openModal('hrm_leave_approve_modal'); },
    async approve() {
      this.setLoading(true);
      try {
        const res = await axios.patch(`/hrms/leaves/${this.approveItem.id}/approve`);
        this.$swalToast(res.data.message || 'Leave approved.');
        this.$closeModal('hrm_leave_approve_modal');
        this.fetchRequests(this.pagination.current_page);
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.setLoading(false); }
    },

    openReject(row) { this.rejectItem = row; this.rejectReason = ''; this.rejectError = ''; this.$openModal('hrm_leave_reject_modal'); },
    async reject() {
      if (!this.rejectReason.trim()) { this.rejectError = 'Rejection reason is required'; return; }
      this.setLoading(true);
      try {
        const res = await axios.patch(`/hrms/leaves/${this.rejectItem.id}/reject`, {
          rejection_reason: this.rejectReason.trim(),
        });
        this.$swalToast(res.data.message || 'Leave rejected.');
        this.$closeModal('hrm_leave_reject_modal');
        this.fetchRequests(this.pagination.current_page);
      } catch (error) {
        if (error.type === 'validation') this.rejectError = (error.errors?.rejection_reason?.[0]) || 'Invalid reason';
        else this.$swalToast(error.message, 'error');
      } finally { this.setLoading(false); }
    },
  },
};
</script>

<template>
  <div>
    <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
      <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
        <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
          Team Requests <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
        </h5>
        <select class="form-select form-select-sm" style="width:auto; min-width:140px;" v-model="statusFilter" @change="fetchRequests(1)">
          <option value="">All Status</option>
          <option v-for="s in statusOptions" :key="s" :value="s">{{ s.charAt(0).toUpperCase() + s.slice(1) }}</option>
        </select>
      </div>

      <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
        <app-table
          :columns="tableColumns" :rows="requests" :pagination="pagination" :loading="loading"
          empty-text="No team leave requests." empty-icon="ti ti-calendar-off"
          @page-change="fetchRequests"
        >
          <template #employee="{ row }">
            <span class="fw-semibold" style="font-size:12.5px; color:#1e293b;">{{ empName(row) }}</span>
          </template>
          <template #leave_type="{ row }">
            <span class="lv-dot" :style="{ background: cardColor(row.leave_type?.color) }"></span>
            {{ row.leave_type?.name || '-' }}

          </template>
          <template #dates="{ row }">
            <span v-if="row.from_date === row.to_date">{{ $formatDate(row.from_date) }}</span>
            <span v-else>{{ $formatDate(row.from_date) }} – {{ $formatDate(row.to_date) }}</span>
          </template>
          <template #total_days="{ row }">{{ row.total_days }}</template>
          <template #session="{ row }">
            <span style="font-size:11.5px; color:#64748b;">{{ sessionLabel(row.session) }}</span>
          </template>
          <template #status="{ row }">
            <span class="lv-st" :class="'lv-' + row.status">{{ row.status }}</span>
          </template>
          <template #reason="{ row }">
            <span style="font-size:12px; color:#64748b;">{{ row.reason || '-' }}</span>
          </template>
          <template #actions="{ row }">
            <div v-if="row.status === 'pending'" class="d-flex gap-1 justify-content-end">
              <button class="btn btn-sm btn-light text-success" title="Approve" @click="confirmApprove(row)"><i class="ti ti-check"></i></button>
              <button class="btn btn-sm btn-light text-danger" title="Reject" @click="openReject(row)"><i class="ti ti-x"></i></button>
            </div>
            <span v-else>-</span>
          </template>
        </app-table>
      </div>
    </div>

    <!-- Approve confirm -->
    <div class="modal fade" id="hrm_leave_approve_modal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center">
            <span class="avatar avatar-xl bg-transparent-success text-success mb-3"><i class="ti ti-calendar-check fs-36"></i></span>
            <h4 class="mb-1">Approve Leave</h4>
            <p class="mb-3">Approve this leave request? Balance will be deducted.</p>
            <div class="d-flex justify-content-center">
              <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">No</a>
              <a href="javascript:void(0);" class="btn btn-success" @click="approve">Yes, Approve</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject reason -->
    <div class="modal fade" id="hrm_leave_reject_modal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Leave</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <label class="lv-lbl">Rejection Reason <span class="text-danger">*</span></label>
            <textarea class="form-control" rows="3" maxlength="500" v-model="rejectReason"
              :class="{ 'is-invalid': rejectError }" placeholder="Why is this being rejected?"></textarea>
            <div v-if="rejectError" class="lv-err">{{ rejectError }}</div>
          </div>
          <div class="modal-footer">
            <a href="javascript:void(0);" class="btn btn-light" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="reject">Reject</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.lv-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.lv-st { display: inline-block; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 20px; text-transform: capitalize; }
.lv-pending { background: #fef3c7; color: #b45309; }
.lv-approved { background: #dcfce7; color: #16a34a; }
.lv-rejected { background: #fee2e2; color: #dc2626; }
.lv-cancelled { background: #f1f5f9; color: #94a3b8; }
.lv-lbl { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.lv-err { font-size: 11px; color: #dc2626; margin-top: 4px; }
.form-control { border-radius: 10px; border-color: #e2e8f0; font-size: 13.5px; }
.form-control:focus { border-color: #a5b4fc; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
</style>